/*
 * Product pages: the merch picker (a size, posted or picked up at your store), the cake picker (your store and its days
 * from the hub, asked once) and the drop picker (a store's own variant). What's held, sold out or on the shelf is the
 * hub's or Shopify's to say; a read that fails says it can't check, never sold out. Holds go to the cart as `ms:added`.
 */
import { hub, storeChoice, setStoreChoice, zoneFor, todayIn, addDays } from "./ms-hub.js";
import { money, dayLabel } from "./ms-format.js";
import { waitView } from "./ms-waitlist.js";

// ── The rules (tested in tests/product.test.mjs) ──────────────────────────────────────────────────────────────────

/** A store's own name: "Bondi" from "Bondi Messina" or a drop's "NSW - Bondi". */
const short = (name) => String(name || "").replace(/\s+messina$/i, "").replace(/^(NSW|ACT|VIC|QLD|SA|WA|TAS|NT)\s+-\s+/i, "").trim();
const same = (a, b) => short(a).toLowerCase() === short(b).toLowerCase();
const sizeKey = (s) => String(s || "").toLowerCase().replace(/\s+/g, "");

/** A drop's variant for a store (its option is the store's name), or null when that store hasn't one to sell. */
export function dropVariant(variants, store) {
  const v = variants.find((x) => same(x.title, store));
  return v && v.available ? v : null;
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const utcDay = (iso) => new Date(iso + "T00:00:00Z");
const isDay = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ""));

/** A drop's pick-up days: its window (the product's pick-up from and to) from the store's today on, two months at most.
 *  None once it's over, and none for a window that doesn't read or ends before it starts. */
export function dropDays(from, to, today) {
  if (!isDay(from) || !isDay(to) || to < from) return [];
  const out = [];
  for (let d = from > today ? from : today; d <= to && out.length < 62; d = addDays(d, 1)) out.push(d);
  return out;
}

/** The window as the price row says it: "Pick up 21 to 24 Dec", "Pick up 30 Nov to 3 Dec" or "Pick up 23 Dec". */
export function windowWords(from, to) {
  const a = utcDay(from), b = utcDay(to);
  const mon = (d) => MONTHS[d.getUTCMonth()].slice(0, 3);
  if (from === to) return `Pick up ${b.getUTCDate()} ${mon(b)}`;
  return `Pick up ${a.getUTCDate()}${mon(a) === mon(b) ? "" : ` ${mon(a)}`} to ${b.getUTCDate()} ${mon(b)}`;
}

/** "21 and 24 December", or "30 November and 3 December": the first and last of a run of days. */
export function dayRange(a, b) {
  const x = utcDay(a), y = utcDay(b);
  return x.getUTCMonth() === y.getUTCMonth() ? `${x.getUTCDate()} and ${y.getUTCDate()} ${MONTHS[y.getUTCMonth()]}` : `${x.getUTCDate()} ${MONTHS[x.getUTCMonth()]} and ${y.getUTCDate()} ${MONTHS[y.getUTCMonth()]}`;
}

/** "Wednesday 23 December": a pick-up date as checkout, the order and staff read it. */
export function longDay(iso) {
  const d = utcDay(iso);
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** The state in a drop's variant name ("NSW - Bondi"), or null for one named by its store alone. */
export function variantState(title) {
  const m = /^(NSW|ACT|VIC|QLD|SA|WA|TAS|NT)\s+-\s+/i.exec(String(title || ""));
  return m ? m[1].toUpperCase() : null;
}

/** What Add puts in Shopify's cart: the store's variant and, with a window, the day as "Pick-up date" (shown at checkout,
 *  on the order and to staff) and as `_pickup_date` (hidden, for the platform). */
export function dropLine(variant, date) {
  return date ? { id: variant.id, quantity: 1, properties: { "Pick-up date": longDay(date), _pickup_date: date } } : { id: variant.id, quantity: 1 };
}

/** The hub's product for a size of a Shopify product (sizes match on their label; a one-size product has one). */
function hubProduct(stock, shopifyProductId, size) {
  const mine = ((stock && stock.products) || []).filter((p) => p.shopifyProductId === String(shopifyProductId));
  if (mine.length === 1 && (!size || !mine[0].size)) return mine[0];
  return mine.find((p) => sizeKey(p.size) === sizeKey(size)) || null;
}

/** Whether a size is on a store's shelf today: "in", "out", or "unknown" when that store's shelf hasn't been read. */
export function shelfFor({ variant, stock, shopifyProductId, storeId }) {
  if (!stock || !((stock.stores || []).some((s) => s.storeId === storeId))) return "unknown";
  const p = hubProduct(stock, shopifyProductId, variant.title === "Default Title" ? null : variant.title);
  const here = p && p.stores.find((s) => s.storeId === storeId);
  return here && here.status === "in" ? "in" : "out";
}

/** The merch page's Add button: what it says and whether it works. With no store yet it opens the store sheet. */
export function merchButton({ mode, available, storeName, checking, failed, shelf, price }) {
  const add = { label: `Add to order · ${price}`, disabled: false };
  if (mode === "posted") return available ? add : { label: "Sold out online", disabled: true };
  if (!storeName) return { label: "Pick your store first", disabled: false, opensStores: true };
  if (checking) return { label: "Checking the shelf…", disabled: true };
  if (failed) return { label: "Can’t check the shelf", disabled: true };
  if (shelf === "out") return { label: `Not on ${storeName}’s shelf today`, disabled: true };
  return add;
}

/** What Add does: a Shopify line for posted, a hub line for a pick-up from your store's shelf, or why it can't. */
export function merchAddFor({ mode, variant, quantity = 1, stock, shopifyProductId, storeId, date }) {
  if (mode === "posted") return { kind: "shopify", body: { id: variant.id, quantity } };
  const p = hubProduct(stock, shopifyProductId, variant.title === "Default Title" ? null : variant.title);
  const store = ((stock && stock.stores) || []).find((s) => s.storeId === storeId);
  const here = p && p.stores.find((s) => s.storeId === storeId);
  if (!p || !here || here.status !== "in") {
    const what = variant.title && variant.title !== "Default Title" ? variant.title : "this";
    return { kind: "none", reason: `${short(store ? store.name : "Your store")} doesn’t have ${what} on the shelf today.` };
  }
  return { kind: "hub", line: { storeId, productId: p.productId, date, quantity } };
}

/** The merch page's store sheet: stores taking website orders, by state, each by its own name, the chosen one marked. */
export function pickupRows(stores, chosenId) {
  const open = (stores || []).filter((s) => s.open && s.open.web);
  if (!open.length) return `<p class="t-small t-soft">No store is taking website orders right now. Have it posted instead.</p>`;
  const states = [...new Set(open.map((s) => s.state || ""))].sort();
  return states.map((st) => `<div class="t-label pick-hd">${esc(st)}</div>` + open.filter((s) => (s.state || "") === st).sort((a, b) => short(a.name).localeCompare(short(b.name)))
    .map((s) => { const on = String(s.id) === String(chosenId); return `<button class="pick-row${on ? " on" : ""}" type="button" data-pick-store="${esc(s.id)}"${on ? ' aria-current="true"' : ""}><span class="t-card" style="flex: 1; text-align: left">${esc(short(s.name))}</span></button>`; }).join("")).join("");
}

/** Under the sizes: which are sold out online ("Every size is sold out online." when none are left). One size says nothing. */
export function outNote(variants) {
  if (!variants || variants.length < 2) return "";
  const out = variants.filter((v) => !v.available).map((v) => v.title);
  if (!out.length) return "";
  if (out.length === variants.length) return "Every size is sold out online.";
  return `${out.length > 1 ? `${out.slice(0, -1).join(", ")} and ${out.at(-1)} are` : `${out[0]} is`} sold out online.`;
}

/** Under the sizes: a size sold out online, and which stores have it on the shelf today (when that's known). */
export function sizeNote(variant, stock, shopifyProductId) {
  if (variant.available) return null;
  const p = hubProduct(stock, shopifyProductId, variant.title);
  const names = p ? p.stores.filter((s) => s.status === "in").map((s) => short((stock.stores.find((x) => x.storeId === s.storeId) || {}).name)).filter(Boolean) : [];
  if (!names.length) return `${variant.title} is sold out online.`;
  const list = names.length > 3 ? `${names.slice(0, 3).join(", ")} and more` : names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names.at(-1)}` : names[0];
  return `${variant.title} is sold out online. ${list} ${names.length > 1 ? "have" : "has"} it on the shelf today.`;
}

const CLOSED = new Set(["store-closed", "closed-day", "too-far", "closed"]);
const NONE = new Set(["sold-out", "hq-full", "toggled-off"]);
const OFF = new Set(["channel-off", "paused", "store-off", "switched-off"]);
/** A day on a cake's strip, from the hub's answer for it. */
/** Before cakes go on sale (Theme settings, Cakes): the week from the on-sale day, greyed, under the store pill, and no
 * note (the line about when orders open says it). null once they're on sale, when the store's own days show. */
export function cakeSoonView(soonIso, storeCount) {
  if (!soonIso) return null;
  const [y, m, d] = soonIso.split("-").map(Number);
  const days = Array.from({ length: 7 }, (_, i) => new Date(Date.UTC(y, m - 1, d + i)).toISOString().slice(0, 10));
  return { days, pill: storeCount ? "Pick a store" : "Your store", note: "" };
}

export function dayState(a) {
  if (!a) return { state: "unknown", words: "Can’t check" };
  if (a.available) return { state: "available", words: typeof a.left === "number" ? `${a.left} left` : a.from === "marrickville" ? "Made to order" : "Available" };
  if (CLOSED.has(a.reason)) return { state: "closed", words: "Can’t do this day" };
  if (NONE.has(a.reason)) return { state: "none", words: "None left" };
  if (OFF.has(a.reason)) return { state: "off", words: "Not taking orders" };
  if (a.reason === "past-date") return { state: "gone", words: "" };
  return { state: "closed", words: "Can’t do this day" };
}

/** The tub page's sizes from the flavours read, or that it can't check (never an empty list). */
export function tubState(r) {
  if (!r || !r.ok || !r.data || !Array.isArray(r.data.tubs)) return { kind: "cant", message: "We can’t check the tubs right now." };
  return { kind: "tubs", tubs: r.data.tubs };
}

const COLS = { BL: "Body length", CW: "Chest width", SL: "Sleeve length", W: "Width", L: "Length", H: "Height" };
/**
 * A product's sizing note as a table: the header line names the columns ("BODY LENGTH (BL), CHEST WIDTH (CW) … IN CM"),
 * each "XS - 66 (BL), 60 (CW), 54 (SL)" line is a row, and the words before and after are kept as they're written.
 * Null when the note isn't laid out that way, so it's shown as written.
 */
export function sizingTable(text) {
  const lines = String(text || "").split(/\r?\n/).map((l) => l.trim());
  const headAt = lines.findIndex((l) => /\(([A-Z]{1,3})\)/.test(l) && !/^\S+\s+-\s+\d/.test(l));
  const rowRe = /^(\S+(?:\s\(\d[^)]*\))?)\s+-\s+(.+)$/;
  const rows = [];
  let firstRow = -1, lastRow = -1;
  lines.forEach((l, i) => {
    const m = rowRe.exec(l);
    if (!m || !/\(\w{1,3}\)/.test(m[2])) return;
    const vals = [...m[2].matchAll(/([\d.]+)\s*\((\w{1,3})\)/g)].map((v) => v[1]);
    if (!vals.length) return;
    rows.push([m[1], ...vals]);
    if (firstRow < 0) firstRow = i;
    lastRow = i;
  });
  if (headAt < 0 || !rows.length) return null;
  const codes = [...lines[headAt].matchAll(/\((\w{1,3})\)/g)].map((m) => m[1]);
  return {
    head: ["Size", ...codes.map((c) => COLS[c] || c)],
    rows,
    before: lines.slice(0, Math.min(headAt, firstRow)).filter(Boolean),
    after: lines.slice(lastRow + 1).filter(Boolean),
  };
}

// ── The pickers (browser only) ────────────────────────────────────────────────────────────────────────────────────

const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
function toast(text) {
  let t = document.querySelector(".toast-live");
  if (!t) { t = document.createElement("div"); t.className = "toast toast-live"; t.setAttribute("role", "status"); document.body.appendChild(t); }
  t.textContent = text; t.hidden = false;
  clearTimeout(toast.timer); toast.timer = setTimeout(() => { t.hidden = true; }, 5000);
}
async function addShopify(body) {
  try {
    const r = await fetch("/cart/add.js", { method: "POST", headers: { "content-type": "application/json", accept: "application/json" }, body: JSON.stringify(body) });
    if (!r.ok) return false;
    document.dispatchEvent(new CustomEvent("ms:shop-cart"));
    return true;
  } catch { return false; }
}

if (typeof window !== "undefined" && typeof customElements !== "undefined") {
  class MsMerchPicker extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.product = JSON.parse(this.querySelector("script[data-product]")?.textContent || "{}");
      this.variant = this.product.variants.find((v) => v.available) || this.product.variants[0];
      this.mode = "posted";
      this.store = storeChoice();
      this.addEventListener("click", (e) => this.onClick(e));
      // The phone's sticky bar sits outside the picker and adds the same way.
      document.addEventListener("click", (e) => { const b = e.target.closest("[data-bar-add]"); if (b && !b.disabled) this.add(); });
      this.addEventListener("ms:track", (e) => { this.mode = e.detail.index === 1 ? "pickup" : "posted"; this.render(); if (this.mode === "pickup") this.loadStock(); });
      this.addEventListener("ms:waitlist", (e) => { this.waitJoined = e.detail.joined; this.render(); });
      // The store sheet sits outside the picker, at the end of the section.
      document.addEventListener("click", (e) => {
        const s = e.target.closest("#merch-stores [data-pick-store]");
        if (s) { this.pickStore(s.dataset.pickStore); return; }
        const again = e.target.closest("#merch-stores [data-merch-stores]");
        if (again) this.openStores(again);
      });
      const label = this.querySelector('[data-name="mode"] [role="radio"]:nth-child(2)');
      if (label && this.store) label.textContent = `Pick-up at ${short(this.store.name)}`;
      const table = this.querySelector("[data-sizing]");
      if (table) this.renderSizing(table);
      // Held back from sale (data-soon): the page has no sizes, no way to get it and no Add, only the photos, the list and
      // the drop-downs, so there's nothing to read or redraw.
      if (this.hasAttribute("data-soon")) return;
      this.loadStock();
      this.render();
    }

    async loadStock() {
      if (!this.store) { this.stock = null; return; }
      const store = this.store;
      // Until this store's shelf is in, nothing reads as on it or off it.
      this.stock = null;
      this.stockFailed = false;
      this.checking = true;
      this.render();
      const r = await hub("merch-stock", { storeId: store.id });
      if (store !== this.store) return;
      this.checking = false;
      this.stock = r.ok ? r.data : null;
      this.stockFailed = !r.ok;
      // A size that isn't on this shelf gives way to one that is.
      if (this.mode === "pickup" && this.shelf(this.variant) === "out") this.variant = this.product.variants.find((v) => this.shelf(v) === "in") || this.variant;
      this.render();
      // Who has a size sold out online is anyone's: the whole list, read once.
      if (!this.allStock) { const all = await hub("merch-stock", {}); if (all.ok) this.allStock = all.data; }
    }

    shelf(v) { return shelfFor({ variant: v, stock: this.stock, shopifyProductId: this.product.id, storeId: this.store?.id }); }

    renderSizing(el) {
      const t = sizingTable(el.dataset.sizing);
      if (!t) return;
      el.innerHTML = t.before.map((l) => `<p class="t-small">${esc(l)}</p>`).join("")
        + `<div class="size-table" role="region" aria-label="Sizes" tabindex="0"><table><thead><tr>${t.head.map((h) => `<th scope="col">${esc(h)}</th>`).join("")}</tr></thead><tbody>`
        + t.rows.map((r) => `<tr>${r.map((c, i) => (i ? `<td>${esc(c)}</td>` : `<th scope="row">${esc(c)}</th>`)).join("")}</tr>`).join("") + `</tbody></table></div>`
        + t.after.map((l) => `<p class="t-small">${esc(l)}</p>`).join("");
    }

    onClick(e) {
      const size = e.target.closest("[data-variant]");
      if (size) {
        const v = this.product.variants.find((x) => String(x.id) === size.dataset.variant);
        const n = this.querySelector("[data-size-note]");
        if (v && this.mode === "pickup" && this.shelf(v) === "out") { if (n) n.textContent = `${short(this.store.name)} doesn’t have ${v.title} on the shelf today.`; }
        else if (v && (v.available || this.mode === "pickup")) { this.variant = v; this.render(); }
        else if (v) { if (n) n.textContent = sizeNote(v, this.allStock, this.product.id); }
        return;
      }
      const thumb = e.target.closest("[data-thumb]");
      if (thumb) {
        const img = this.querySelector("[data-main-photo]");
        if (img) { img.src = thumb.dataset.thumb; img.alt = thumb.dataset.alt || ""; }
        this.querySelectorAll("[data-thumb]").forEach((t) => t.toggleAttribute("aria-current", t === thumb));
        return;
      }
      if (e.target.closest("[data-merch-stores]") || (e.target.closest("[data-add]") && this.mode === "pickup" && !this.store)) { this.openStores(e.target.closest("button")); return; }
      if (e.target.closest("[data-shelf-again]")) { this.loadStock(); return; }
      if (e.target.closest("[data-add]")) this.add();
    }

    async openStores(from) {
      const sheet = document.getElementById("merch-stores");
      const list = sheet?.querySelector("[data-merch-store-list]");
      if (!sheet || !list) return;
      sheet.open(from);
      list.innerHTML = `<p class="t-small t-soft">Finding the stores…</p>`;
      const r = await hub("stores", {});
      const stores = r.ok ? (Array.isArray(r.data?.stores) ? r.data.stores : Array.isArray(r.data) ? r.data : null) : null;
      list.innerHTML = stores ? pickupRows(stores, this.store?.id) : `<p class="t-small t-soft">We can’t check the stores right now. <button class="t-link" type="button" data-merch-stores>Try again</button></p>`;
      this.stores = stores || [];
    }

    pickStore(id) {
      const s = this.stores.find((x) => String(x.id) === String(id));
      if (!s) return;
      this.store = s;
      setStoreChoice({ id: s.id, name: s.name, state: s.state });
      const label = this.querySelector('[data-name="mode"] [role="radio"]:nth-child(2)');
      if (label) label.textContent = `Pick-up at ${short(s.name)}`;
      document.getElementById("merch-stores")?.close();
      this.render();
      this.loadStock();
    }

    render() {
      if (this.hasAttribute("data-soon")) return;
      this.querySelectorAll("[data-variant]").forEach((b) => {
        const v = this.product.variants.find((x) => String(x.id) === b.dataset.variant);
        // Nothing reads as chosen when every size is sold out online and it's being posted.
        const offShelf = this.mode === "pickup" && this.shelf(v) === "out";
        const on = v === this.variant && (this.mode === "pickup" ? !offShelf : v.available);
        b.setAttribute("aria-checked", String(on));
        b.classList.toggle("on", on);
        // One Tab stop for the sizes, on the chosen one; the arrow keys move between them (ms-ui.js).
        b.tabIndex = on ? 0 : -1;
        const off = this.mode === "posted" ? !v.available : offShelf;
        b.toggleAttribute("aria-disabled", off);
        if (!off) b.removeAttribute("aria-label");
        else b.setAttribute("aria-label", this.mode === "posted" ? `${v.title}, sold out online` : `${v.title}, not on ${short(this.store.name)}’s shelf today`);
      });
      const sizes = [...this.querySelectorAll("[data-variant]")];
      if (sizes.length && !sizes.some((b) => b.tabIndex === 0)) sizes[0].tabIndex = 0;
      const note = this.querySelector("[data-size-note]");
      if (note) note.textContent = outNote(this.product.variants);
      const how = this.querySelector("[data-how]");
      const icon = (n) => this.querySelector(`[data-how-icons]`)?.content.querySelector(`[data-i="${n}"]`)?.innerHTML || "";
      if (how) how.innerHTML = icon(this.mode === "posted" ? "truck" : "store") + `<span class="how-tx">` + (this.mode === "posted"
        ? `<b>Posted from Marrickville</b><span class="t-small t-soft">Postage is worked out at checkout.</span>`
        : this.store ? `<b>Pick it up at ${esc(short(this.store.name))}</b><span class="t-small t-soft">${this.checking ? "Checking what’s on the shelf…" : this.stockFailed ? `We can’t check the shelf right now. <button class="t-link" type="button" data-shelf-again>Try again</button>` : "With your order, from what’s on the shelf today."}</span>`
        : `<b>Pick it up at your store</b><span class="t-small t-soft"><button class="t-link" type="button" data-merch-stores>Pick your store</button> first.</span>`) + `</span>`;
      // Sold out online and being posted: the waitlist takes the Posted panel's and the button's place, and the phone bar's.
      const wait = this.querySelector("ms-waitlist");
      const view = wait ? waitView({ soldOut: !this.product.available, mode: this.mode, joined: !!this.waitJoined }) : "none";
      if (wait) wait.hidden = view === "none";
      if (how) how.hidden = view !== "none";
      const price = money(this.variant.price);
      [...this.querySelectorAll("[data-add]"), ...document.querySelectorAll("[data-bar-add]")].forEach((b) => {
        // With no store yet, the button opens the store sheet rather than sitting disabled.
        const s = merchButton({ mode: this.mode, available: this.variant.available, storeName: this.store ? short(this.store.name) : null, checking: this.checking, failed: this.stockFailed, shelf: this.shelf(this.variant), price });
        b.disabled = s.disabled;
        b.hidden = view !== "none";
        b.querySelector("[data-add-label]").textContent = s.label;
      });
      document.querySelectorAll("[data-bar-wait]").forEach((b) => { b.hidden = view !== "offer"; });
      const bar = document.querySelector(".prod-bar");
      if (bar) bar.classList.toggle("bar-off", view === "joined");
    }

    async add() {
      const store = this.store;
      const today = store ? todayIn(zoneFor(store.state)) : null;
      const a = merchAddFor({ mode: this.mode, variant: this.variant, quantity: 1, stock: this.stock, shopifyProductId: this.product.id, storeId: store?.id, date: today });
      if (a.kind === "none") { toast(a.reason); return; }
      if (a.kind === "shopify") { toast((await addShopify(a.body)) ? `Added. ${this.product.title} is in your order.` : "We couldn’t add it just now. Try again in a moment."); return; }
      document.dispatchEvent(new CustomEvent("ms:added", { detail: { line: a.line, title: this.product.title, store, kind: "merch", size: this.variant.title === "Default Title" ? null : this.variant.title } }));
    }
  }

  class MsCakePicker extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.shopifyId = this.dataset.product;
      this.title = this.dataset.title;
      this.addEventListener("click", (e) => this.onClick(e));
      this.delivery = false;
      this.addEventListener("ms:track", (e) => { this.delivery = e.detail.index === 1; this.load(); });
      // The store sheet sits outside the picker, at the end of the page.
      document.addEventListener("click", (e) => { if (e.target.closest("#cake-stores [data-pick-store]")) this.onClick(e); });
      this.load();
    }

    async load() {
      const run = this.querySelector("[data-days]");
      const note = this.querySelector("[data-day-note]");
      const say = (html) => { note.innerHTML = html; };
      // Only the newest read draws: a store or Pick-up/Delivery change while one is out makes the older one stale.
      const gen = (this.gen = (this.gen || 0) + 1);
      const r = await hub("stores", { shopifyProductId: this.shopifyId });
      if (gen !== this.gen) return;
      if (!r.ok) { run.innerHTML = ""; say(`We can’t check the days right now. <button class="t-link" type="button" data-reload>Try again</button>`); this.button(null); return; }
      this.productId = r.data.productId;
      this.stores = r.data.stores || [];
      const saved = storeChoice();
      this.store = (saved && this.stores.find((s) => s.id === saved.id)) || null;
      this.renderStores();
      // Before cakes go on sale: that week greyed, and the line about when orders open says the rest.
      const soon = cakeSoonView(this.dataset.soon, this.stores.length);
      this.soon = !!soon;
      if (soon) {
        this.querySelector("[data-cake-store]").textContent = this.store ? short(this.store.name) : soon.pill;
        this.days = soon.days.map((date) => ({ date, state: "soon", words: "Not open yet" }));
        this.chosen = null;
        this.renderDays();
        return;
      }
      this.querySelector("[data-cake-store]").textContent = this.store ? short(this.store.name) : this.stores.length ? "Pick a store" : "Not online";
      if (!this.stores.length) { run.innerHTML = ""; say("This cake isn’t sold online right now. Ask at your store."); this.button(null); return; }
      if (!this.store) { run.innerHTML = ""; say("Pick your store and its days show here."); this.button(null); return; }
      run.innerHTML = Array.from({ length: 7 }, () => `<span class="day skel" aria-hidden="true"></span>`).join("");
      say(`Checking ${esc(short(this.store.name))}’s days…`);
      const d = await hub("days", { storeId: this.store.id, productId: this.productId, quantity: 1, delivery: this.delivery });
      if (gen !== this.gen) return;
      if (!d.ok) { run.innerHTML = ""; say(`We can’t check ${esc(short(this.store.name))}’s days right now. <button class="t-link" type="button" data-reload>Try again</button>`); this.button(null); return; }
      this.days = d.data.days.map((x) => ({ date: x.date, ...dayState(x) })).filter((x) => x.state !== "gone");
      this.today = d.data.today;
      this.chosen = (this.days.find((x) => x.state === "available") || {}).date || null;
      this.renderDays();
    }

    renderStores() {
      const list = document.querySelector("[data-cake-store-list]");
      if (!list) return;
      list.innerHTML = this.stores.length ? this.stores.map((s) => `<button class="pick-row${this.store && this.store.id === s.id ? " on" : ""}" type="button" data-pick-store="${esc(s.id)}"><span class="t-card" style="flex: 1; text-align: left">${esc(short(s.name))}</span><span class="t-small t-soft">${esc(s.state)}</span></button>`).join("")
        : `<p class="t-small t-soft">No store is selling this cake online right now.</p>`;
    }

    renderDays() {
      const run = this.querySelector("[data-days]");
      const month = this.querySelector("[data-month]");
      const week = this.days.slice(0, 14);
      if (month && week[0]) month.textContent = new Date(week[0].date + "T00:00:00Z").toLocaleDateString("en-AU", { month: "long", timeZone: "UTC" });
      run.setAttribute("role", "radiogroup");
      run.innerHTML = week.map((x) => {
        const l = dayLabel(x.date, this.today);
        const dow = new Date(x.date + "T00:00:00Z").toLocaleDateString("en-AU", { weekday: "short", timeZone: "UTC" }).toUpperCase();
        const on = x.date === this.chosen;
        const stop = on || (!this.chosen && x === (week.find((y) => y.state === "available") || week[0]));
        return `<button class="day${on ? " on" : ""}${x.state === "available" ? "" : " off"}" type="button" role="radio" aria-checked="${on}" tabindex="${stop ? 0 : -1}" data-date="${x.date}"${x.state === "available" ? "" : ' aria-disabled="true"'} aria-label="${esc(l)}, ${esc(x.words)}"><span class="d">${dow}</span><span class="n">${Number(x.date.slice(8))}</span></button>`;
      }).join("");
      if (this.soon) { this.querySelector("[data-day-note]").textContent = ""; this.button(null); return; }
      const chosen = this.days.find((x) => x.date === this.chosen);
      this.querySelector("[data-day-note]").textContent = chosen ? `${dayLabel(chosen.date, this.today)} at ${short(this.store.name)}. ${chosen.words}.` : `${short(this.store.name)} can’t take an order for this cake in these days. Try another store.`;
      this.button(chosen && chosen.state === "available" ? chosen : null);
    }

    button(day) {
      const b = this.querySelector("[data-add]");
      if (!b) return;
      b.disabled = !day;
      b.querySelector("[data-add-label]").textContent = day ? `Add to order · ${dayLabel(day.date, this.today)}` : "Pick a day";
    }

    onClick(e) {
      if (e.target.closest("[data-reload]")) { this.load(); return; }
      const d = e.target.closest("[data-date]");
      if (d) { const x = this.days.find((y) => y.date === d.dataset.date); if (x && x.state === "available") { this.chosen = x.date; this.renderDays(); } else if (x) this.querySelector("[data-day-note]").textContent = `${dayLabel(x.date, this.today)}: ${x.words}.`; return; }
      const s = e.target.closest("[data-pick-store]");
      // The pick is remembered (as the Order page's is), so load() finds it and every page after agrees.
      if (s) { this.store = this.stores.find((x) => x.id === s.dataset.pickStore); if (this.store) setStoreChoice({ id: this.store.id, name: this.store.name, state: this.store.state }); document.getElementById("cake-stores")?.close(); this.querySelector("[data-cake-store]").textContent = short(this.store.name); this.load(); return; }
      const rail = e.target.closest("[data-earlier], [data-later]");
      if (rail) { this.querySelector("[data-days]").scrollBy({ left: rail.matches("[data-later]") ? 280 : -280, behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" }); return; }
      if (e.target.closest("[data-add]") && this.chosen) {
        document.dispatchEvent(new CustomEvent("ms:added", { detail: { line: { storeId: this.store.id, productId: this.productId, date: this.chosen, quantity: 1 }, title: this.title, store: this.store, kind: "cake" } }));
      }
    }
  }

  class MsDropPicker extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.product = JSON.parse(this.querySelector("script[data-product]")?.textContent || "{}");
      // With a pick-up window a store leads on to its days (data-drop-day); without one it's added straight away.
      this.window = this.dataset.from && this.dataset.to ? { from: this.dataset.from, to: this.dataset.to } : null;
      this.addEventListener("click", (e) => this.onClick(e));
    }

    async onClick(e) {
      if (e.target.closest("[data-drop-change]")) { this.showStores(); return; }
      if (e.target.closest("[data-earlier], [data-later]")) {
        const later = e.target.closest("[data-later]");
        this.querySelector("[data-days]").scrollBy({ left: later ? 280 : -280, behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
        return;
      }
      const d = e.target.closest("[data-date]");
      if (d) { if (this.days.includes(d.dataset.date)) { this.chosen = d.dataset.date; this.renderDays(); } return; }
      if (e.target.closest("[data-drop-add]")) { this.add(); return; }
      const b = e.target.closest("[data-drop-store]");
      if (!b) return;
      const v = dropVariant(this.product.variants, b.dataset.dropStore);
      if (!v) { toast(`${short(b.dataset.dropStore)} hasn’t any left.`); return; }
      if (this.window) { this.showDays(v); return; }
      b.disabled = true;
      toast((await addShopify(dropLine(v, null))) ? `Added. It’s for pick-up at ${short(b.dataset.dropStore)}, and it checks out on its own.` : "We couldn’t add it just now. Try again in a moment.");
      b.disabled = false;
    }

    /** The store's days: the window from that store's own today on. */
    showDays(variant) {
      this.variant = variant;
      this.today = todayIn(zoneFor(variantState(variant.title)));
      this.days = dropDays(this.window.from, this.window.to, this.today);
      if (!this.days.includes(this.chosen)) this.chosen = null;
      this.querySelector("[data-drop-stores]").hidden = true;
      const panel = this.querySelector("[data-drop-day]");
      panel.hidden = false;
      this.querySelector("[data-drop-name]").textContent = short(variant.title);
      this.querySelector("[data-drop-change]").setAttribute("aria-label", `Change the store, now ${short(variant.title)}`);
      this.renderDays();
      if (panel.getBoundingClientRect().top < 0) panel.scrollIntoView({ block: "start", behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
      (this.querySelector('[data-date][tabindex="0"]') || this.querySelector("[data-drop-change]")).focus({ preventScroll: true });
    }

    showStores() {
      this.querySelector("[data-drop-day]").hidden = true;
      this.querySelector("[data-drop-stores]").hidden = false;
      const back = this.variant && [...this.querySelectorAll("[data-drop-store]")].find((x) => x.dataset.dropStore === this.variant.title);
      (back || this.querySelector("[data-drop-store]:not([disabled])"))?.focus();
    }

    renderDays() {
      const run = this.querySelector("[data-days]");
      const store = short(this.variant.title);
      const month = this.querySelector("[data-month]");
      if (month) month.textContent = this.days[0] ? new Date(this.days[0] + "T00:00:00Z").toLocaleDateString("en-AU", { month: "long", timeZone: "UTC" }) : "";
      run.setAttribute("role", "radiogroup");
      run.innerHTML = this.days.map((date, i) => {
        const on = date === this.chosen;
        const dow = new Date(date + "T00:00:00Z").toLocaleDateString("en-AU", { weekday: "short", timeZone: "UTC" }).toUpperCase();
        return `<button class="day${on ? " on" : ""}" type="button" role="radio" aria-checked="${on}" tabindex="${on || (!this.chosen && i === 0) ? 0 : -1}" data-date="${date}" aria-label="${esc(longDay(date))}"><span class="d">${dow}</span><span class="n">${Number(date.slice(8))}</span></button>`;
      }).join("");
      // The arrows show only when the days run past the panel.
      const arrows = this.querySelector(".dates-hd .btns");
      if (arrows) arrows.hidden = run.scrollWidth <= run.clientWidth + 2;
      const note = this.querySelector("[data-day-note]");
      if (!this.days.length) note.textContent = `Pick-up finished on ${longDay(this.window.to).replace(/^\w+ /, "")}.`;
      else if (this.chosen) note.innerHTML = `<b>${esc(longDay(this.chosen))} at ${esc(store)}.</b>`;
      else note.textContent = this.days.length === 1 ? `Pick-up is on ${longDay(this.days[0])}.` : `Pick a day between ${dayRange(this.days[0], this.days.at(-1))}.`;
      const add = this.querySelector("[data-drop-add]");
      add.disabled = !this.chosen;
      add.querySelector("[data-add-label]").textContent = this.chosen ? `Add · ${dayLabel(this.chosen, this.today)}` : this.days.length ? "Pick a day" : "Pick-up has finished";
    }

    async add() {
      if (!this.variant || !this.chosen) return;
      const b = this.querySelector("[data-drop-add]");
      const l = dayLabel(this.chosen, this.today);
      b.disabled = true;
      const ok = await addShopify(dropLine(this.variant, this.chosen));
      toast(ok ? `Added. It’s for pick-up at ${short(this.variant.title)} ${l === "Today" || l === "Tomorrow" ? l.toLowerCase() : `on ${l}`}, and it checks out on its own.` : "We couldn’t add it just now. Try again in a moment.");
      b.disabled = false;
    }
  }

  for (const [name, el] of [["ms-merch-picker", MsMerchPicker], ["ms-cake-picker", MsCakePicker], ["ms-drop-picker", MsDropPicker]]) if (!customElements.get(name)) customElements.define(name, el);
}
